/**
 * Dégradation gracieuse du filtre : si l'affichage décroche (< 20 img/s),
 * l'analyse de la vidéo de l'ami passe à 8 img/s, puis s'arrête (main fixe).
 * Le jeu (détection du rire sur MA vidéo) n'est jamais sacrifié au filtre.
 */
export type TrackingLevel = 'full' | 'reduced' | 'fixed';

/** Cadence d'analyse de la vidéo de l'ami selon le niveau (0 = plus de suivi). */
export const DETECTION_FPS: Record<TrackingLevel, number> = { full: 15, reduced: 8, fixed: 0 };

const LEVELS: readonly TrackingLevel[] = ['full', 'reduced', 'fixed'];

export interface PerfGovernorOptions {
  readonly minFps?: number;
  readonly recoverFps?: number;
  /** Durée d'observation avant de dégrader. */
  readonly windowMs?: number;
  /** Durée fluide avant de remonter d'un niveau. */
  readonly recoverMs?: number;
  /** Au-delà, l'onglet était en arrière-plan : on recommence la mesure. */
  readonly maxGapMs?: number;
}

export interface PerfGovernor {
  /** À appeler à chaque image affichée (`t` en ms). */
  frame(t: number): TrackingLevel;
  level(): TrackingLevel;
}

export function createPerfGovernor({
  minFps = 20,
  recoverFps = 28,
  windowMs = 2000,
  recoverMs = 5000,
  maxGapMs = 500,
}: PerfGovernorOptions = {}): PerfGovernor {
  let index = 0;
  let start: number | null = null;
  let frames = 0;
  let last: number | null = null;

  const restart = (t: number): void => {
    start = t;
    frames = 0;
  };

  return {
    frame(t) {
      if (start === null || (last !== null && t - last > maxGapMs)) restart(t);
      last = t;
      frames++;
      const span = t - (start ?? t);
      const fps = span > 0 ? ((frames - 1) * 1000) / span : Infinity;
      if (span >= windowMs && fps < minFps && index < LEVELS.length - 1) {
        index++;
        restart(t);
      } else if (span >= recoverMs) {
        if (fps >= recoverFps && index > 0) index--;
        restart(t);
      }
      return LEVELS[index] ?? 'fixed';
    },
    level: () => LEVELS[index] ?? 'fixed',
  };
}
